import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { useSocketStore } from '@/stores/socketStore';
import { connectSocket, disconnectSocket } from '@/lib/socket';
import type { Intervention } from '@/lib/types';

/**
 * Branche le client Socket.IO au montage de l'app authentifiée.
 * Invalide automatiquement le cache TanStack Query sur événements intervention.*.
 */
export function useSocketSync(): void {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setConnected = useSocketStore((s) => s.setConnected);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!accessToken) return;
    const socket = connectSocket(accessToken);

    // Un RDV créé/déplacé/supprimé change AUSSI les créneaux libres (['slots'])
    // et les blocs occupés de l'agenda du formulaire (['day-interventions']).
    // Sans ces invalidations, la modale de création peut proposer un créneau
    // déjà pris (faux libre) jusqu'à expiration du refetchInterval 30 s.
    const invalidate = () => {
      void queryClient.invalidateQueries({ queryKey: ['interventions'] });
      void queryClient.invalidateQueries({ queryKey: ['slots'] });
      void queryClient.invalidateQueries({ queryKey: ['day-interventions'] });
    };

    // Resync à chaque (re)connexion : rattrape les événements émis pendant
    // une coupure réseau (sinon ils sont perdus et le cache reste périmé).
    const onConnect = () => {
      setConnected(true);
      invalidate();
    };
    // Socket tombé → on réactive le polling « filet de sécurité » des hooks.
    const onDisconnect = () => setConnected(false);

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onDisconnect);
    socket.on('intervention.created', invalidate);
    socket.on('intervention.updated', invalidate);
    socket.on('intervention.deleted', invalidate);
    socket.on('intervention.cascaded', (payload: { sourceId: string; impacted: unknown[] }) => {
      invalidate();
      // hint visuel possible Phase 7
      void payload;
    });

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onDisconnect);
      socket.off('intervention.created', invalidate);
      socket.off('intervention.updated', invalidate);
      socket.off('intervention.deleted', invalidate);
      disconnectSocket();
      // Évite de rester bloqué « déconnecté » après démontage (logout) :
      // l'optimisme par défaut reprend à la prochaine session.
      setConnected(true);
    };
  }, [accessToken, queryClient, setConnected]);
}

export type InterventionDetail = Omit<Intervention, 'moto'> & {
  description: string | null;
  notesInternes: string | null;
  arriveeMotoAt: string | null;
  debutInterventionAt: string | null;
  finInterventionAt: string | null;
  livraisonAt: string | null;
  cascadeOriginId: string | null;
  moto: Intervention['moto'] & { annee: number | null; vin: string | null };
  statusHistory: Array<{
    id: string;
    typeChangement: 'statut' | 'duree' | 'mecanicien' | 'creneau' | 'cascade';
    ancienneValeur: unknown;
    nouvelleValeur: unknown;
    commentaire: string | null;
    createdAt: string;
    user: { id: string; prenom: string; nom: string };
  }>;
};
